import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useRef } from 'react'
import { Toaster, ToastId } from '@fluentui/react-components'

export const toasterId = 'hammockToaster'

/**
 * Which toasts are currently on screen.
 *
 * Fluent has no way to ask a toaster whether a toast is still visible, while dispatching a toast whose id is already
 * taken is silently ignored. So we track it ourselves, and it has to be shared rather than kept in a hook: the
 * component that owns a toast may unmount and mount again under the same id, and a fresh set would make it dispatch a
 * second toast instead of updating the first.
 */
const ToastActiveContext = createContext<Set<ToastId> | undefined>(undefined)

export function useActiveToastIds(): Set<ToastId> {
    const active = useContext(ToastActiveContext)

    if (!active) {
        throw new Error('useActiveToastIds() must be used within an AppToastProvider')
    }

    return active
}

/**
 * Provides the bookkeeping that useAppToast needs. Mounted high in the tree so that anything can raise a toast; the
 * toaster that renders them is mounted separately, see AppToasterOutlet.
 */
export function AppToastProvider(props: PropsWithChildren): ReactElement {
    const active = useRef(new Set<ToastId>())

    return <ToastActiveContext.Provider value={active.current}>{props.children}</ToastActiveContext.Provider>
}

/**
 * Renders the single toaster of the application.
 *
 * This deliberately sits deep in the tree rather than next to the FluentProvider: Fluent renders toast content inside
 * the toaster's own React tree, so a toast can only use the contexts that are available where the toaster is mounted.
 * Queue toasts read the queue, axios, translations and the popup context, so the toaster has to sit below all of them.
 */
export default function AppToasterOutlet(): ReactElement {
    return <Toaster toasterId={toasterId} position="bottom-end" limit={5} offset={{ horizontal: 48, vertical: 48 }} pauseOnHover pauseOnWindowBlur />
}
